import {
  DocumentTextIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { DocumentFileItem, DocumentUpload } from '@ths/shared/components';
import { add } from 'date-fns';
import { FORM_ERROR } from 'final-form';
import arrayMutators from 'final-form-arrays';
import { HotelForm as Form } from 'hotel/HotelForm';
import { useMemo } from 'react';
import { Field } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';

import {
  NewReservationRequestClientTeamMemberInput,
  NewReservationRequestInput,
  ReservationRequiredField,
  ClientPod,
  RequiredFieldInput,
} from '../../generated/graphql';
import { useClientRequiredFieldsAndPod } from '../../hooks/useClientRequiredFieldsAndPod';
import { StagedFile } from '../../hooks/useStagedFileUpload';
import {
  DOCUMENTS_ACCEPT,
  formatFileSize,
  getFileIcon,
} from '../../utilities/fileUtils';
import {
  getGuestLevelFields,
  GUEST_FIELD_MAPPING,
  initializeRequiredFieldsArray,
  isBookingLevelField,
} from '../../utilities/guestFieldMapping';
import { parseDateForPicker } from '../../utilities/parseDateForPicker';
import {
  required,
  validDODID,
  composeValidators,
} from '../../utilities/validators';
import { ActionsMenu } from '../ActionsMenu';
import { Button, PrimaryButton } from '../Button';
import ClientTeamMemberArrayField from '../Inputs/FormInputs/ClientTeamMemberArrayField';
import FormDateField from '../Inputs/FormInputs/FormDateField';
import FormDropdown from '../Inputs/FormInputs/FormDropdown';
import TextField from '../Inputs/FormInputs/TextField';
import RequiredFieldItem from '../RequiredFieldItem';
import { SectionHeader } from '../SectionHeader';

export type NewReservationFormValues = Omit<
  NewReservationRequestInput,
  'existingGuests' | 'newGuests'
> & {
  guests: NewReservationRequestClientTeamMemberInput[];
  requiredFields: RequiredFieldInput[];
};

// Allow partial guest data for initial values (e.g., pre-filling traveler info)
type NewReservationFormInitialValues = Partial<
  Omit<NewReservationFormValues, 'guests'>
> & {
  guests?: Partial<NewReservationRequestClientTeamMemberInput>[];
};

type NewReservationFormProps = {
  onSubmit: (values: NewReservationFormValues) => void;
  initialValues?: NewReservationFormInitialValues;
  readonly?: boolean;
  isTraveler?: boolean;
  stagedFiles?: StagedFile[];
  onStageFile?: (
    file: File,
    displayName?: string,
  ) => Promise<StagedFile | null>;
  isUploadingFile?: boolean;
};

export default function NewReservationForm({
  onSubmit,
  initialValues,
  readonly = false,
  isTraveler = false,
  stagedFiles = [],
  onStageFile,
  isUploadingFile = false,
}: NewReservationFormProps) {
  const { clientRequiredFields, clientPod } = useClientRequiredFieldsAndPod();

  // Get only guest-specific fields with null safety
  const guestFields = getGuestLevelFields(
    clientRequiredFields?.map((field) => field.value) || [],
  );

  // Get order-level fields to display in each guest card
  // Filter by scope if available, otherwise use the legacy logic
  const orderFields =
    clientRequiredFields
      ?.filter(
        (field) =>
          field.scope === 'ORDER' ||
          (!field.scope && isBookingLevelField(field.value)),
      )
      .map((field) => field.value) || [];

  // Parent/session updates must not reinitialize the form and discard its guests.
  const formInitialValues = useMemo(
    () => ({
      ...initialValues,
      requiredFields:
        initialValues?.requiredFields ??
        initializeRequiredFieldsArray(
          clientRequiredFields?.map((field) => field.value) || [],
        ),
    }),
    [initialValues, clientRequiredFields],
  );

  // Form-level validation: require guests OR files
  const validateForm = (values: NewReservationFormValues) => {
    const errors: { [FORM_ERROR]?: string } = {};
    const hasGuests = values.guests && values.guests.length > 0;
    const hasFiles = stagedFiles && stagedFiles.length > 0;
    if (!hasGuests && !hasFiles) {
      // Keep the form-wide requirement separate from nested guest field errors.
      errors[FORM_ERROR] = 'Please add at least one guest or attach a file';
    }
    return errors;
  };

  const handleFileUpload = async (file: File, displayName: string) => {
    if (onStageFile) {
      await onStageFile(file, displayName || undefined);
    }
  };

  const hasFiles = stagedFiles && stagedFiles.length > 0;

  return (
    <Form
      onSubmit={onSubmit}
      validate={validateForm}
      mutators={{
        ...arrayMutators,
      }}
      initialValues={formInitialValues}
    >
      {({ handleSubmit, form, error, submitFailed }) => (
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-6 gap-6">
            <SectionHeader
              className="col-span-4 mb-0 pb-2.5 text-base font-medium"
              headline="Reservation Details"
            />

            {/* Manual Guest Entry Section */}
            <div className="col-span-4 sm:col-start-1">
              {!readonly && !isTraveler && (
                <ClientTeamMemberArrayField
                  arrayName="guests"
                  labelText="Add guests"
                  placeholder="Search by ID or Name"
                  disabled={readonly}
                  allowCreateGuest={true}
                />
              )}
            </div>

            {/* File Attachment Section - matching staff portal style */}
            {!readonly && onStageFile && !isTraveler && (
              <div className="col-span-4 space-y-4">
                <span className="section-label">
                  Attachments{' '}
                  <span className="font-normal text-gray-400">(optional)</span>
                </span>

                {/* File List */}
                {hasFiles ? (
                  <div className="rounded-lg border border-gray-200 bg-white">
                    <ul className="divide-y divide-gray-200">
                      {stagedFiles.map((stagedFile) => (
                        <li
                          key={stagedFile.id}
                          className="px-5 py-3.5 transition-colors hover:bg-gray-50"
                        >
                          <DocumentFileItem
                            file={{
                              id: stagedFile.id,
                              displayFilename: stagedFile.displayFilename,
                              createdAt: new Date().toISOString(),
                            }}
                            canEdit={false}
                            onView={() => {
                              const url = URL.createObjectURL(stagedFile.file);
                              window.open(url, '_blank');
                            }}
                            onDelete={() => {}}
                            ActionsMenu={ActionsMenu}
                          />
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div className="rounded-lg border border-gray-200 bg-white px-6 py-8">
                    <div className="text-center">
                      <DocumentTextIcon className="mx-auto size-10 text-gray-300" />
                      <p className="mt-2 text-sm text-gray-500">
                        No documents attached
                      </p>
                    </div>
                  </div>
                )}

                {/* Upload Section */}
                <DocumentUpload
                  accept={`${DOCUMENTS_ACCEPT},image/*`}
                  maxSize={100}
                  description="PDF, Word, Excel, or image files up to 100MB"
                  onConfirm={handleFileUpload}
                  isUploading={isUploadingFile}
                  getFileIcon={getFileIcon}
                  formatFileSize={formatFileSize}
                />
              </div>
            )}

            <FieldArray name="guests" disabled={readonly}>
              {({ fields }) => (
                <>
                  {fields.map((guest, index) => {
                    // Check if a guest is new by checking if it has an ID
                    const guests = form.getFieldState(`guests`)?.value;
                    const guestValues = guests && guests[index];

                    return (
                      // Guest Reservation Card
                      <div
                        key={guest}
                        className="col-span-4 grid grid-cols-8 gap-6 rounded-md border border-gray-300 bg-gray-100 p-5"
                      >
                        {/* Header */}

                        {!guestValues?.id && (
                          <div className="col-span-8 flex flex-row items-start justify-start rounded-md bg-yellow-50 p-4 text-sm text-yellow-800">
                            <>
                              <ExclamationTriangleIcon
                                width={25}
                                className="mr-3 font-bold"
                              />
                              <p>
                                This person has not yet been added to the Master
                                PAX List. Please fill out all fields to add this
                                person to the Master PAX List and their
                                reservation details.
                              </p>
                            </>
                          </div>
                        )}
                        <div className="col-span-7 text-gray-900">
                          <h2 className="mb-2.5 font-medium">
                            {guestValues?.id
                              ? `${guestValues.lastName}, ${guestValues.firstName}`
                              : 'New Guest'}
                          </h2>
                        </div>
                        <div className="col-span-1 flex items-start justify-end">
                          <Button
                            type="button"
                            onClick={() => fields.remove(index)}
                            disabled={readonly || (isTraveler && index === 0)}
                          >
                            Cancel
                          </Button>
                        </div>

                        {/* New Guest Fields (Dynamic based on client config) */}
                        {!guestValues?.id && (
                          <>
                            {/* Always required fields: First Name and Last Name */}
                            <div className="col-span-4">
                              <Field
                                labelText="First Name"
                                name={`${guest}.firstName`}
                                component={TextField}
                                disabled={readonly}
                                validate={required}
                              />
                            </div>
                            <div className="col-span-4">
                              <Field
                                labelText="Last Name"
                                name={`${guest}.lastName`}
                                component={TextField}
                                disabled={readonly}
                                validate={required}
                              />
                            </div>

                            {/* Dynamic guest fields based on client configuration */}
                            {guestFields.map((fieldType) => {
                              const fieldConfig = clientRequiredFields?.find(
                                (f) => f.value === fieldType,
                              );
                              if (!fieldConfig) return null;

                              // Use the mapped property name for the form field
                              const fieldName =
                                GUEST_FIELD_MAPPING[
                                  fieldType as keyof typeof GUEST_FIELD_MAPPING
                                ] || fieldType;

                              // Determine which fields are required
                              const baseRequiredFields = [
                                ReservationRequiredField.Rank,
                                ReservationRequiredField.DodId,
                              ];

                              // Gender is only required for Government Solutions clients
                              const isGovernmentSolutions =
                                clientPod === ClientPod.GovernmentSolutions;
                              if (
                                isGovernmentSolutions &&
                                fieldType === ReservationRequiredField.Gender
                              ) {
                                baseRequiredFields.push(
                                  ReservationRequiredField.Gender,
                                );
                              }

                              const isRequired =
                                baseRequiredFields.includes(fieldType);

                              return (
                                <div key={fieldType} className="col-span-4">
                                  <RequiredFieldItem
                                    name={`${guest}.${fieldName}`}
                                    label={fieldConfig.label}
                                    fieldType={fieldType}
                                    required={isRequired}
                                    // Apply appropriate validation based on field type
                                    validation={
                                      fieldType ===
                                      ReservationRequiredField.DodId
                                        ? composeValidators(
                                            required,
                                            validDODID,
                                          )
                                        : isRequired
                                          ? required
                                          : undefined
                                    }
                                    disabled={readonly}
                                  />
                                </div>
                              );
                            })}
                          </>
                        )}

                        {/* Order-level Required Fields (like Control Number, etc.) */}
                        {orderFields.length > 0 && (
                          <>
                            <SectionHeader
                              className="col-span-8 mb-0 pb-2.5 text-base font-medium"
                              headline="Booking Details"
                            />
                            <FieldArray name="requiredFields">
                              {({ fields: requiredFieldsArray }) => (
                                <>
                                  {orderFields.map((fieldType) => {
                                    const fieldConfig =
                                      clientRequiredFields?.find(
                                        (f) => f.value === fieldType,
                                      );
                                    if (!fieldConfig) return null;

                                    // Find the index of this field in the requiredFields array
                                    const fieldIndex =
                                      requiredFieldsArray.value?.findIndex(
                                        (f: RequiredFieldInput) =>
                                          f.name === fieldType,
                                      );

                                    if (
                                      fieldIndex === undefined ||
                                      fieldIndex < 0
                                    ) {
                                      return null;
                                    }

                                    return (
                                      <div
                                        key={fieldType}
                                        className="col-span-4"
                                      >
                                        <RequiredFieldItem
                                          name={`requiredFields[${fieldIndex}].value`}
                                          label={fieldConfig.label}
                                          fieldType={fieldType}
                                          required={false} // Order fields are typically optional
                                          disabled={readonly}
                                        />
                                      </div>
                                    );
                                  })}
                                </>
                              )}
                            </FieldArray>
                          </>
                        )}

                        {/* Room Details Fields */}
                        <div className="col-span-8 col-start-1">
                          <Field
                            labelText="Room type"
                            name={`${guest}.roomType`}
                            component={FormDropdown}
                            validate={required}
                            options={[
                              'Single',
                              'Double',
                              'Triple',
                              'Quadruple',
                            ]}
                            disabled={readonly}
                            renderOption={(roomBedType: string) => (
                              <span className="block truncate font-normal">
                                {roomBedType}
                              </span>
                            )}
                          />
                        </div>
                        <div className="col-span-4 col-start-1">
                          <Field
                            labelText="Check-in date"
                            name={`${guest}.checkin`}
                            component={FormDateField}
                            placeholderText="-"
                            validate={required}
                            disabled={readonly}
                            selectsStart
                          />
                        </div>
                        <div className="col-span-4">
                          <Field
                            labelText="Check-out date"
                            name={`${guest}.checkout`}
                            component={FormDateField}
                            placeholderText="-"
                            validate={required}
                            disabled={readonly}
                            selectsEnd
                            minDate={add(
                              parseDateForPicker(guestValues?.checkin) ??
                                new Date(),
                              {
                                days: 1,
                              },
                            )}
                          />
                        </div>
                        <div className="col-span-8">
                          <Field
                            labelText="Remarks"
                            name={`${guest}.remarks`}
                            component={TextField}
                            disabled={readonly}
                            multiline
                            rows={6}
                          />
                        </div>

                        {/* Previous Hotel Fields */}
                        <SectionHeader
                          className="col-span-4 mb-0 pb-2.5 text-base font-medium"
                          headline="Previous Hotel Information If Transferring (Optional)"
                        />
                        <div className="col-span-8 col-start-1">
                          <Field
                            labelText="Hotel name"
                            name={`${guest}.previousHotelName`}
                            component={TextField}
                            disabled={readonly}
                          />
                        </div>
                        <div className="col-span-8 col-start-1">
                          <Field
                            labelText="Hotel address"
                            name={`${guest}.previousHotelAddress`}
                            component={TextField}
                            disabled={readonly}
                          />
                        </div>
                      </div>
                    );
                  })}
                  <div className="col-span-4 col-start-1">
                    {error && submitFailed && (
                      <span className="text-xs text-red-800">{error}</span>
                    )}
                  </div>
                </>
              )}
            </FieldArray>

            {/* Preferred Hotel Fields */}
            <SectionHeader
              className="col-span-4 mb-0 pb-2.5 text-base font-medium"
              headline="Preferred Hotel Information (Optional)"
            />
            <div className="col-span-2 col-start-1">
              <Field
                labelText="Hotel name"
                name={`preferredHotelName`}
                component={TextField}
                disabled={readonly}
              />
            </div>
            <div className="col-span-2 col-start-1">
              <Field
                labelText="Hotel address"
                name={`preferredHotelAddress`}
                component={TextField}
                disabled={readonly}
              />
            </div>
            <div className="col-span-2 col-start-1">
              <Field
                labelText="Hotel email"
                name={`preferredHotelEmail`}
                component={TextField}
                disabled={readonly}
              />
            </div>
            <div className="col-span-2 col-start-1">
              <Field
                labelText="Hotel phone number"
                name={`preferredHotelPhone`}
                component={TextField}
                disabled={readonly}
              />
            </div>

            {/* Submit Form */}
            {!readonly && (
              <div className="col-span-6">
                <PrimaryButton text="Submit request" type="submit" />
              </div>
            )}
          </div>
        </form>
      )}
    </Form>
  );
}
